import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ClinicalTools = () => {
  const [activeFeature, setActiveFeature] = useState(null);

  const features = [
    {
      id: 'xray',
      title: 'AI X-Ray Analysis',
      description: 'Advanced AI-powered analysis of dental X-rays for accurate diagnosis',
      icon: '🦷',
      link: '/ai-xray-analysis'
    },
    {
      id: 'virtual',
      title: 'AI Virtual Front Desk',
      description: 'Intelligent virtual assistant for patient check-in and support',
      icon: '🤖',
      link: '/ai-virtual-front-desk'
    },
    {
      id: 'chatbot',
      title: 'AI Chatbot',
      description: 'Smart chatbot for instant patient support and information',
      icon: '💬',
      link: '/ai-chatbot'
    },
    {
      id: 'clinical',
      title: 'AI Clinical Assistant',
      description: 'AI-powered assistant for clinical decision support',
      icon: '👨‍⚕️',
      link: '/ai-clinical-assistant'
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              AI-Powered Clinical Tools
            </h1>
            <p className="text-xl text-gray-600">
              Enhance clinical efficiency and accuracy with our suite of AI-powered dental tools
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E2A4A] mb-4">Key Features</h2>
              <ul className="space-y-4">
                {features.map((feature) => (
                  <li 
                    key={feature.id}
                    className={`flex items-start space-x-3 p-4 rounded-xl cursor-pointer transition-all ${
                      activeFeature === feature.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setActiveFeature(feature.id)}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                      {feature.icon}
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                      <Link 
                        to={feature.link}
                        className="inline-block mt-2 text-orange-500 hover:text-orange-600 font-medium"
                      >
                        Read More →
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#001233] text-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold mb-4">Benefits</h2>
              <ul className="space-y-4">
                {[
                  { value: '95%', label: 'Diagnostic Accuracy' },
                  { value: '60%', label: 'Time Savings' },
                  { value: '24/7', label: 'AI Support' }
                ].map((benefit, index) => (
                  <li 
                    key={index}
                    className="flex items-center space-x-3 hover:scale-105 transition-transform"
                  >
                    <div className="text-3xl font-bold text-orange-500">{benefit.value}</div>
                    <div className="text-gray-300">{benefit.label}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Content */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold text-[#1E2A4A] mb-6">How It Works</h2>
              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Data Processing',
                    description: 'AI analyzes clinical data and patient information'
                  },
                  {
                    step: 2,
                    title: 'Smart Analysis',
                    description: 'AI provides real-time insights and recommendations'
                  },
                  {
                    step: 3,
                    title: 'Clinical Support',
                    description: 'AI assists in diagnosis and treatment planning'
                  }
                ].map((step) => (
                  <div 
                    key={step.step}
                    className="flex items-start space-x-4 hover:scale-102 transition-transform"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 shadow-xl text-white">
              <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="mb-6">Transform your clinical workflow with AI</p>
              <Link 
                to="/schedule-demo"
                className="block w-full bg-white text-orange-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors text-center"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalTools; 